<template>
  <div>
    <v-slide-y-transition>
      <div
        v-if="pickerActive"
        class="feature-picker-banner"
      >
        <v-icon
          color="white"
          class="mr-2"
        >
          mdi-cursor-default-click
        </v-icon>
        <span>Picker mode: browse normally, then click the pin badge on a highlighted element to attach it.</span>
        <v-btn
          small
          text
          class="ml-4"
          color="white"
          @click="cancel"
        >
          Cancel
        </v-btn>
      </div>
    </v-slide-y-transition>

    <button
      v-if="pickerActive && pinStyle"
      type="button"
      class="feature-picker-pin"
      :style="pinStyle"
      @click="selectHovered"
    >
      <v-icon
        small
        color="white"
      >
        mdi-target
      </v-icon>
    </button>
  </div>
</template>

<script>
  export default {
    name: 'FeaturePicker',

    data: () => ({
      hoveredEl: null,
      pinStyle: null,
    }),

    computed: {
      pickerActive () {
        return this.$store.getters['companyFeatures/pickerActive']
      },
    },

    watch: {
      pickerActive (active) {
        if (!active) {
          this.clearHover()
        }
      },
    },

    created () {
      // Listeners live for the whole session; handlers no-op unless picker
      // mode is active. Highlighting only ever reads elements/positions and
      // never touches the app's own click handling, so normal navigation
      // and button actions are completely unaffected while picking.
      document.addEventListener('mouseover', this.onMouseOver, true)
      window.addEventListener('scroll', this.onReposition, true)
      window.addEventListener('resize', this.onReposition, true)
    },

    beforeDestroy () {
      document.removeEventListener('mouseover', this.onMouseOver, true)
      window.removeEventListener('scroll', this.onReposition, true)
      window.removeEventListener('resize', this.onReposition, true)
    },

    methods: {
      findMarkedElement (target) {
        return target?.closest ? target.closest('[data-feature-key]') : null
      },

      clearHover () {
        if (this.hoveredEl) {
          this.hoveredEl.classList.remove('feature-picker-hover')
          this.hoveredEl = null
        }
        this.pinStyle = null
      },

      updatePinStyle () {
        if (!this.hoveredEl) {
          this.pinStyle = null
          return
        }

        const rect = this.hoveredEl.getBoundingClientRect()
        this.pinStyle = {
          top: `${Math.max(rect.top - 12, 4)}px`,
          left: `${Math.min(rect.right - 4, window.innerWidth - 32)}px`,
        }
      },

      onReposition () {
        if (this.pickerActive && this.hoveredEl) {
          this.updatePinStyle()
        }
      },

      onMouseOver (e) {
        if (!this.pickerActive) {
          return
        }

        // Hovering the pin itself shouldn't clear/relocate it.
        if (e.target?.closest && e.target.closest('.feature-picker-pin')) {
          return
        }

        const marked = this.findMarkedElement(e.target)
        if (marked === this.hoveredEl) {
          return
        }
        this.clearHover()
        if (marked) {
          marked.classList.add('feature-picker-hover')
          this.hoveredEl = marked
          this.updatePinStyle()
        }
      },

      selectHovered () {
        const marked = this.hoveredEl
        if (!marked) {
          return
        }

        const key = marked.getAttribute('data-feature-key')
        const label = marked.getAttribute('data-feature-label') || key
        const icon = marked.getAttribute('data-feature-icon') || ''

        this.clearHover()
        const returnPath = this.$store.getters['companyFeatures/pickerReturnPath']
        this.$store.dispatch('companyFeatures/stopPicker')

        // Stored in Vuex (not just emitted) because a cross-page pick means
        // the Feature Management page isn't mounted yet to hear a signal
        // fired before navigation completes.
        this.$store.commit('companyFeatures/setPendingPick', { key, label, icon })
        this.$signalHub.$emit('feature-picker-selected', { key, label, icon })

        if (returnPath && this.$route.path !== returnPath) {
          this.$router.push(returnPath)
        }
      },

      cancel () {
        this.clearHover()
        this.$store.dispatch('companyFeatures/stopPicker')
      },
    },
  }
</script>

<style>
  .feature-picker-banner {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    background: #1976d2;
    color: #fff;
    padding: 10px 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    border-radius: 24px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
    max-width: calc(100vw - 32px);
    pointer-events: auto;
  }

  .feature-picker-hover {
    outline: 3px solid #ff5252 !important;
    outline-offset: 2px;
  }

  .feature-picker-pin {
    position: fixed;
    z-index: 10000;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #ff5252;
    border: 2px solid #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transform: translate(-50%, -50%);
  }
</style>
